import React, { useState, useRef } from 'react';
import { Upload, X, File, FileText, Image, Video, Loader2, Eye, Link as LinkIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import FileViewer from './FileViewer';

const ProofFileUpload = ({
    proofFiles = [],
    onProofFilesChange,
    proofUrl,
    onProofUrlChange,
    error,
    isSubmitting,
    readOnly = false
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDragActive, setIsDragActive] = useState(false);
    const [previewFile, setPreviewFile] = useState(null);
    const fileInputRef = useRef(null);

    const getFileIcon = (fileType) => {
        if (fileType?.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500 dark:text-blue-400" />;
        if (fileType?.startsWith('video/')) return <Video className="w-4 h-4 text-purple-500 dark:text-purple-400" />;
        if (fileType === 'application/pdf') return <FileText className="w-4 h-4 text-red-500 dark:text-red-400" />;
        return <File className="w-4 h-4 text-gray-500 dark:text-slate-400" />;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleFiles = async (files) => {
        if (files.length === 0) return;
        if (proofFiles.length + files.length > 3) { toast.error('Maximum 3 files allowed'); return; }
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) { toast.error(`Files exceed 10 MB limit`); return; }

        setUploading(true);
        setUploadProgress(0);
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));
            const token = localStorage.getItem('token');
            const response = await api.post('/files/upload-proof', formData, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
            });
            if (response.data.success) {
                onProofFilesChange([...proofFiles, ...response.data.files]);
                toast.success(`${response.data.files.length} file(s) uploaded`);
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.message || 'Upload failed');
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (fileToRemove) => {
        onProofFilesChange(proofFiles.filter(f => f.file_key !== fileToRemove.file_key));
        toast.success('File removed');
    };

    return (
        <div className="space-y-3">
            {/* Upload Area */}
            {!readOnly && (
                <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!uploading && proofFiles.length < 3) setIsDragActive(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(false); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragActive(false); if (!uploading && proofFiles.length < 3) handleFiles(Array.from(e.dataTransfer.files)); }}
                    onClick={() => !uploading && proofFiles.length < 3 && fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-6 transition-all cursor-pointer flex flex-col items-center gap-2 text-center ${
                        isDragActive
                            ? 'border-blue-300 dark:border-blue-500/40 bg-blue-50/50 dark:bg-blue-500/5'
                            : error
                                ? 'border-red-300 dark:border-red-500/30 bg-red-50/30 dark:bg-red-500/5'
                                : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                    } ${(uploading || proofFiles.length >= 3) ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                    <Upload className={`w-6 h-6 ${isDragActive ? 'text-blue-500' : 'text-gray-400 dark:text-slate-500'}`} />
                    <div>
                        <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
                            {uploading ? 'Uploading...' : 'Click or drag files here'}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">PDF, JPG, PNG, DOC — Max 10MB</p>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => handleFiles(Array.from(e.target.files))}
                        disabled={uploading || proofFiles.length >= 3 || isSubmitting || readOnly}
                        className="hidden"
                    />
                </div>
            )}

            {error && <p className="text-xs text-red-600 dark:text-red-400 font-medium">{error}</p>}

            {/* Upload Progress */}
            {uploading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Uploading... {uploadProgress}%</span>
                </div>
            )}

            {/* File List */}
            {proofFiles.length > 0 && (
                <div className="space-y-1.5">
                    {proofFiles.map((file, index) => (
                        <div
                            key={index}
                            className="flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700/60 rounded-lg group hover:border-gray-300 dark:hover:border-slate-600 transition-colors"
                        >
                            <div className="flex-shrink-0">{getFileIcon(file.file_type)}</div>
                            <div
                                className="flex-1 min-w-0 cursor-pointer"
                                onClick={() => setPreviewFile(file)}
                            >
                                <p className="text-sm font-medium text-gray-800 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                    {file.file_name}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-slate-500">{formatFileSize(file.file_size)}</p>
                            </div>
                            <button
                                onClick={() => setPreviewFile(file)}
                                className="text-xs text-gray-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex-shrink-0"
                            >
                                <Eye className="w-4 h-4" />
                            </button>
                            {!readOnly && (
                                <button
                                    onClick={() => !isSubmitting && handleRemoveFile(file)}
                                    disabled={isSubmitting}
                                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {readOnly && proofFiles.length === 0 && (
                <div className="text-center py-6 text-gray-400 dark:text-slate-500 text-sm">
                    No files attached
                </div>
            )}

            {/* Optional URL Input */}
            {onProofUrlChange && !readOnly && (
                <div className="pt-3 border-t border-gray-100 dark:border-slate-700/40">
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1.5">
                        Or provide a link
                    </label>
                    <input
                        type="url"
                        value={proofUrl || ''}
                        onChange={(e) => onProofUrlChange(e.target.value)}
                        disabled={isSubmitting}
                        placeholder="https://..."
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                    />
                </div>
            )}

            <FileViewer file={previewFile} isOpen={!!previewFile} onClose={() => setPreviewFile(null)} />
        </div>
    );
};

export default ProofFileUpload;
